import { useState } from 'react';
import { api } from '../../lib/api';
import type { Page, PublicGradeRegisterEntry } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * Public hygiene grade register — packages/modules/m25-public-health/src/routes/public.ts.
 * Genuinely no-bearer-token endpoints (mounted before requireAuth in the
 * module's own app.ts); included here as a staff reference/handout screen —
 * e.g. confirming what a door-placard QR resolves to — not because staff
 * need special access to it.
 */
export function GradeRegister() {
  const [q, setQ] = useState('');
  const [district, setDistrict] = useState('');
  const [qrToken, setQrToken] = useState('');
  const [qrResult, setQrResult] = useState<(PublicGradeRegisterEntry & { found: boolean }) | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrBusy, setQrBusy] = useState(false);

  const qs = new URLSearchParams({ limit: '50' });
  if (q) qs.set('q', q);
  if (district) qs.set('district', district);
  const listQ = useApiQuery<Page<PublicGradeRegisterEntry>>(`/api/v1/m25/public/grade-register?${qs.toString()}`);

  const lookUpQr = async () => {
    setQrBusy(true);
    setQrError(null);
    setQrResult(null);
    try {
      setQrResult(await api.get(`/api/v1/m25/public/grade-register/${encodeURIComponent(qrToken.trim())}`));
    } catch {
      setQrError('Lookup failed');
    } finally {
      setQrBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Public Hygiene Grade Register" subtitle="The same no-login register a member of the public sees scanning a door placard." />

      <div className="mb-5 card p-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Look up by door-placard QR token</div>
        <div className="flex gap-2">
          <input className="field-input !w-72" value={qrToken} onChange={(e) => setQrToken(e.target.value)} placeholder="Grade QR token…" />
          <button className="btn-secondary shrink-0" onClick={lookUpQr} disabled={qrBusy || !qrToken.trim()}>
            Look up
          </button>
        </div>
        {qrError ? (
          <div className="mt-2">
            <ErrorNote message={qrError} />
          </div>
        ) : null}
        {qrResult ? (
          !qrResult.found ? (
            <p className="mt-2 text-xs text-slate-400">No grade on file for that token.</p>
          ) : (
            <div className="mt-2 rounded-lg bg-slate-50 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{qrResult.premisesName}</span>
                {qrResult.grade ? <StatusBadge status={qrResult.grade} /> : <span className="text-xs text-slate-400">No grade yet</span>}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {qrResult.district} · <StatusBadge status={qrResult.operatingStatus} />
              </div>
            </div>
          )
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-56" placeholder="Search premises name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input !w-44" placeholder="District…" value={district} onChange={(e) => setDistrict(e.target.value)} />
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No graded premises match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Premises</th>
                <th className="table-head">Type</th>
                <th className="table-head">District</th>
                <th className="table-head">Grade</th>
                <th className="table-head">Operating</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.refNo} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{r.premisesName}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.premisesType} />
                  </td>
                  <td className="table-cell">{r.district}</td>
                  <td className="table-cell">{r.grade ? <StatusBadge status={r.grade} /> : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.operatingStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
